import { useEffect, useMemo, useRef, useState } from 'react';

import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import type { Query } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';

import type { RosterUser } from '../types';
import { useOrgAccess } from './useOrgAccess';

/** Profile photos are optional; a missing or unreadable profile keeps the initials. */
const fetchProfilePhoto = async (userId: string): Promise<readonly [string, string] | null> => {
  try {
    const profileSnapshot = await getDoc(doc(db, FSCollections.USERS, userId));
    const photoURL = profileSnapshot.exists() ? profileSnapshot.data()?.photoURL : undefined;
    if (typeof photoURL === 'string' && photoURL.trim()) {
      return [userId, photoURL.trim()] as const;
    }
  } catch {
    // Fall through to initials.
  }
  return null;
};

interface RosterState {
  usersByQuery: Record<number, RosterUser[]>;
  /** Queries (by index) that delivered their first snapshot or errored. */
  responded: Record<number, true>;
  queryCount: number;
  error: Error | null;
}

interface UseActiveUsersRosterReturn {
  users: RosterUser[];
  loading: boolean;
  error: Error | null;
}

/**
 * Realtime users roster for the organization.
 *
 * Org-wide roles use a single listener; division-scoped users get one listener
 * per accessible division (results merged and deduped). The full roster is kept
 * in memory so filtering, sorting and counts are exact; the list paginates
 * client-side.
 */
export const useActiveUsersRoster = (orgId: string): UseActiveUsersRosterReturn => {
  const { isAdmin, accessibleDivisionIds, accessKey } = useOrgAccess(orgId);
  const requestedProfileIdsRef = useRef(new Set<string>());
  const isMountedRef = useRef(true);
  const [profilePhotos, setProfilePhotos] = useState<Record<string, string>>({});
  const [state, setState] = useState<RosterState>({
    usersByQuery: {},
    responded: {},
    queryCount: 1,
    error: null,
  });

  useEffect(() => {
    if (!orgId) {
      setState({ usersByQuery: {}, responded: {}, queryCount: 0, error: null });
      return undefined;
    }

    const usersRef = collection(db, FSCollections.ORGANIZATIONS, orgId, FSCollections.USERS);
    const queries: Query[] = isAdmin
      ? [query(usersRef, where('disabled', '==', false))]
      : accessibleDivisionIds.map(divId => query(usersRef, where(`divisions.${divId}`, '!=', null)));

    if (queries.length === 0) {
      setState({ usersByQuery: {}, responded: {}, queryCount: 0, error: null });
      return undefined;
    }

    setState({ usersByQuery: {}, responded: {}, queryCount: queries.length, error: null });

    // First-response tracking lives INSIDE the state (idempotent updates):
    // external mutable flags race with React's deferred updater execution and
    // can leave the loading flag stuck forever.
    const unsubscribes = queries.map((q, index) =>
      onSnapshot(
        q,
        snapshot => {
          const users = snapshot.docs.map(doc => ({ ...(doc.data() as RosterUser), id: doc.id }));
          setState(prev => ({
            ...prev,
            usersByQuery: { ...prev.usersByQuery, [index]: users },
            responded: { ...prev.responded, [index]: true },
          }));
        },
        error => {
          setState(prev => ({
            ...prev,
            responded: { ...prev.responded, [index]: true },
            error,
          }));
        },
      ),
    );

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
    // accessKey already encodes orgId, isAdmin and the accessible division list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey]);

  const users = useMemo(() => {
    const byId = new Map<string, RosterUser>();
    Object.values(state.usersByQuery).forEach(queryUsers => {
      queryUsers.forEach(user => {
        // Division queries cannot filter disabled server-side (no composite index).
        if (user.disabled === false && !byId.has(user.id)) {
          byId.set(user.id, user);
        }
      });
    });
    return Array.from(byId.values()).map(user => ({
      ...user,
      photoURL: user.photoURL || profilePhotos[user.id] || undefined,
    }));
  }, [state.usersByQuery, profilePhotos]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Requested ids are never retried, so in-flight results must survive roster
  // updates; only an unmount may discard them.
  useEffect(() => {
    const usersWithoutPhoto = users.filter(user =>
      !user.photoURL?.trim() && !user.photoUrl?.trim() && !user.avatarURL?.trim() &&
      !requestedProfileIdsRef.current.has(user.id),
    );
    if (usersWithoutPhoto.length === 0) return;
    usersWithoutPhoto.forEach(user => requestedProfileIdsRef.current.add(user.id));

    const loadProfilePhotos = async () => {
      const results = await Promise.all(usersWithoutPhoto.map(user => fetchProfilePhoto(user.id)));
      if (!isMountedRef.current) return;
      const photos = Object.fromEntries(results.filter(
        (result): result is readonly [string, string] => result !== null,
      ));
      if (Object.keys(photos).length > 0) {
        setProfilePhotos(previous => ({ ...previous, ...photos }));
      }
    };
    void loadProfilePhotos();
  }, [users]);

  const loading = Object.keys(state.responded).length < state.queryCount;

  return { users, loading, error: state.error };
};
